import { useEffect, useState } from "react";
import { Prompt, Route, Switch, Redirect, useHistory, useLocation } from "react-router-dom";
import ConfirmationPrompt from "../components/controls/ConfirmationPrompt";
import Home from "../pages/Home";
import Login from "../pages/Login";
import Verify from "../pages/Verify";
import ResetPassword from "../pages/ResetPassword";
import StartTrial from "../pages/StartTrial";
import PubSub from "../services/PubSub";
import Spinner from "../components/controls/Spinner";
import { useTranslation } from "react-i18next";
import InfoModal from "../components/controls/InfoModal";
import DeploymentInfoToast from "../components/deployment/DeploymentInfoToast";
import GuestHome from "../pages/GuestHome";
import { useGetUser } from "./Hooks";

const Router = () => {
    const [translate] = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const [user] = useGetUser();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmedNavigation, setConfirmedNavigation] = useState(0);
    const [currentLocation, setCurrentLocation] = useState("");

    useEffect(() => {
        const timerInterval = 5000;
        const subscriptionId = PubSub.subscribe("timer-start", (deploymentId, callbackFunction) => {
            if (!window.deployTimer) {
                window.deployTimer = {};
            }

            clearInterval(window.deployTimer[deploymentId]);
            window.deployTimer[deploymentId] = setInterval(async () => {
                callbackFunction(deploymentId);
            }, timerInterval);
        });

        const stopSubscriptionId = PubSub.subscribe("timer-stop", (deploymentId) => {
            if (window.deployTimer[deploymentId]) {
                clearInterval(window.deployTimer[deploymentId]);
                PubSub.publish("refresh-deployment");
            }
        });
        return () => {
            PubSub.unsubscribe("timer-start", subscriptionId);
            PubSub.unsubscribe("timer-stop", stopSubscriptionId);
        }
    }, []);

    useEffect(() => {
        const unsavedChangesSubId = PubSub.subscribe("has-unsaved-changes", () => {
            setConfirmedNavigation((prev) => prev + 1);
        });
        const nounsavedChangesSubId = PubSub.subscribe("no-unsaved-changes", () => {
            setConfirmedNavigation((prev) => prev === 0 ? 0 : prev - 1);
        });

        return () => {
            PubSub.unsubscribe("has-unsaved-changes", unsavedChangesSubId);
            PubSub.unsubscribe("no-unsaved-changes", nounsavedChangesSubId);
        };
    }, [location]);

    const handleBlockedNavigation = (nextLocation) => {
        if (confirmedNavigation > 0) {
            setShowConfirmation(true);
            setCurrentLocation(nextLocation);
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (confirmedNavigation === 0 && currentLocation) {
            const pathToRoute = currentLocation.pathname + currentLocation.search;
            setCurrentLocation("");
            history.push(pathToRoute);
        }
    }, [confirmedNavigation, currentLocation, history]);

    const handleConfirm = () => {
        setConfirmedNavigation(0);
        setShowConfirmation(false);
        PubSub.publish("no-unsaved-changes");
    };

    const handleCancel = () => {
        setCurrentLocation("");
        setShowConfirmation(false);
    };

    return (
        <>
            <Spinner />
            <InfoModal />
            <Prompt message={handleBlockedNavigation} />
            <ConfirmationPrompt show={showConfirmation} message={translate("OTM_UNSAVED_CHANGES")}
                onConfirm={handleConfirm}
                onCancel={handleCancel} />
            <Switch>
                <Route path="/auth">
                    <Login />
                </Route>

                <Route path="/verify">
                    <Verify />
                </Route>

                <Route path="/reset-password">
                    <ResetPassword />
                </Route>

                <Route path="/start-trial">
                    <StartTrial />
                </Route>

                <Route path="/guest/:id?/:objectSubTypeId?/:objectSubTypeVersion?">
                    <GuestHome />
                </Route>
                {!user?.isGuest ?
                    <Route path="/:objectType?/:id?/:objectSubType?/:objectSubTypeId?/:objectSubTypeVersion?">
                        <Home />
                    </Route> :
                    <Redirect to="/guest" />
                }
            </Switch>
            <DeploymentInfoToast />
        </>
    );
};

export default Router;
